import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONTACT_KIND_LABELS, CONTACT_KINDS, type Contact, type ContactKind, type Paginated } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { DuplicateWarning } from './DuplicateWarning';
import { RelationPicker } from './relationTypes';

/**
 * Kontakt anlegen oder bearbeiten. Die Art (Person oder Firma) wird beim Anlegen gewählt und lässt sich danach nicht mehr
 * ändern, denn Ansprechpartner hängen daran.
 */
export function ContactFormPage() {
  const { id } = useParams();
  const isCreate = !id;
  const navigate = useNavigate();
  const client = useQueryClient();

  const existing = useQuery({ queryKey: ['contact', id], queryFn: async () => (await api<{ data: Contact }>('GET', `/contacts/${id}`)).data, enabled: !isCreate });

  // „Ansprechpartner hinzufügen“ in einer Firma öffnet das Formular mit dieser Firma vorbelegt
  const [params] = useSearchParams();
  const presetCompany = isCreate ? (params.get('company') ?? '') : '';

  // Zur Auswahl die ersten 100 Firmen nach Name. Bei mehr Firmen braucht die Auswahl eine Suche (siehe KON-047).
  const companies = useQuery({
    queryKey: ['contacts', { kind: 'company', for: 'select' }],
    queryFn: async () => (await api<Paginated<Contact>>('GET', '/contacts?kind=company&perPage=100&sort=name')).data,
  });

  const [kind, setKind] = useState<ContactKind>('person');
  const [companyId, setCompanyId] = useState(presetCompany);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [street, setStreet] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [note, setNote] = useState('');
  const [relationTypeIds, setRelationTypeIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    const c = existing.data;
    if (loaded.current || !c) return;
    loaded.current = true;
    setKind(c.kind);
    setCompanyId(c.companyId ?? '');
    setName(c.name);
    setEmail(c.email ?? '');
    setPhone(c.phone ?? '');
    setStreet(c.street ?? '');
    setPostalCode(c.postalCode ?? '');
    setCity(c.city ?? '');
    setNote(c.note ?? '');
    setRelationTypeIds(c.relationTypeIds);
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () => {
      const fields = { name: name.trim(), email: email.trim(), phone: phone.trim(), street: street.trim(), postalCode: postalCode.trim(), city: city.trim(), note: note.trim(), relationTypeIds };
      // Beim Ändern löscht eine leere Eingabe den Wert, beim Anlegen bleibt das Feld einfach leer
      const company = kind === 'person' ? companyId : '';
      return isCreate
        ? api<{ data: Contact }>('POST', '/contacts', { kind, ...(company ? { companyId: company } : {}), ...fields })
        : api<{ data: Contact }>('PATCH', `/contacts/${id}`, { ...fields, ...(kind === 'person' ? { companyId: company || null } : {}) });
    },
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['contacts'] });
      client.setQueryData(['contact', res.data.id], res.data);
      navigate(`/kontakte/${res.data.id}`, { replace: true });
    },
    onError: (e) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (e instanceof ApiError) {
        setErrors(e.status === 422 ? e.fields : {});
        setFormError(e.status === 422 ? 'Bitte prüfen Sie Ihre Eingaben.' : e.message);
      } else {
        setFormError('Der Kontakt konnte nicht gespeichert werden.');
      }
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    save.mutate();
  }

  if (!isCreate && existing.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isCreate && (existing.isError || !existing.data)) {
    return (
      <div className="card narrow">
        <h1>Kontakt nicht gefunden</h1>
        <Link to="/kontakte">Zur Übersicht</Link>
      </div>
    );
  }

  const back = isCreate ? '/kontakte' : `/kontakte/${id}`;
  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  return (
    <>
      <p><Link to={back} className="back-link">← {isCreate ? 'Alle Kontakte' : 'Zurück zum Kontakt'}</Link></p>
      <div className="page-head">
        <h1>{isCreate ? 'Neuer Kontakt' : `${existing.data!.name} bearbeiten`}</h1>
      </div>

      <form className="card" onSubmit={submit} noValidate>
        {formError && <div className="alert alert--error" role="alert">{formError}</div>}
        <div className="form-grid">
          <label className="field">
            <span>Art <abbr title="Pflichtfeld">*</abbr></span>
            <select className="input" value={kind} disabled={!isCreate} onChange={(e) => setKind(e.target.value as ContactKind)} aria-invalid={!!errors.kind}>
              {CONTACT_KINDS.map((k) => <option key={k} value={k}>{CONTACT_KIND_LABELS[k]}</option>)}
            </select>
            {error('kind')}
            {!isCreate && <small className="muted">Die Art lässt sich nach dem Anlegen nicht ändern.</small>}
          </label>
          <label className="field">
            <span>{kind === 'company' ? 'Firmenname' : 'Name'} <abbr title="Pflichtfeld">*</abbr></span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" aria-invalid={!!errors.name} />
            {error('name')}
          </label>
          {kind === 'person' && (
            <label className="field">
              <span>Firma</span>
              <select className="input" value={companyId} onChange={(e) => setCompanyId(e.target.value)} aria-invalid={!!errors.companyId}>
                <option value="">Keine (Privatperson)</option>
                {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                {/* Eine Firma, die nicht unter den ersten 100 steht, bleibt trotzdem gewählt */}
                {companyId && !companies.data?.some((c) => c.id === companyId) && <option value={companyId}>Aktuell zugeordnete Firma</option>}
              </select>
              {error('companyId')}
              <small className="muted">Als Ansprechpartner dieser Firma. Die Firma legen Sie zuerst als Kontakt der Art „Firma“ an.</small>
            </label>
          )}
          <label className="field">
            <span>E-Mail</span>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" aria-invalid={!!errors.email} />
            {error('email')}
          </label>
          <label className="field">
            <span>Telefon</span>
            <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" aria-invalid={!!errors.phone} />
            {error('phone')}
          </label>
          <label className="field">
            <span>Straße und Hausnummer</span>
            <input className="input" value={street} onChange={(e) => setStreet(e.target.value)} autoComplete="off" aria-invalid={!!errors.street} />
            {error('street')}
          </label>
          <label className="field">
            <span>Postleitzahl</span>
            <input className="input" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} autoComplete="off" inputMode="numeric" aria-invalid={!!errors.postalCode} />
            {error('postalCode')}
          </label>
          <label className="field">
            <span>Ort</span>
            <input className="input" value={city} onChange={(e) => setCity(e.target.value)} autoComplete="off" aria-invalid={!!errors.city} />
            {error('city')}
          </label>
        </div>
        <div className="field">
          <span className="field__label">Beziehung</span>
          <RelationPicker value={relationTypeIds} onChange={setRelationTypeIds} invalid={!!errors.relationTypeIds} />
          {error('relationTypeIds')}
          <small className="muted">Eine oder mehrere, zum Beispiel Kunde und Partner. Die Auswahl pflegen Sie in der Einrichtung unter „Kontakte“.</small>
        </div>
        <DuplicateWarning email={email} phone={phone} excludeId={id} />
        <label className="field">
          <span>Interne Notiz</span>
          <textarea className="input textarea" value={note} onChange={(e) => setNote(e.target.value)} maxLength={5000} placeholder="Nur für Ihr Team sichtbar" />
          {error('note')}
        </label>

        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Wird gespeichert …' : isCreate ? 'Kontakt anlegen' : 'Speichern'}
          </button>
          <Link className="btn btn--secondary" to={back}>Abbrechen</Link>
        </div>
      </form>
    </>
  );
}
